import { ReactNode } from 'react';
import { useLocale } from '@/hooks/useLocale';
import { I18NEXUS_API_KEY } from '@/constants/env';
import { I18NextProvider } from '@simplywallst/hooks/useTranslation';

async function fetchLocales(locale: string, ns: string) {
  const loadPath = `https://api.i18nexus.com/project_resources/translations/${locale}/${ns}.json?api_key=${I18NEXUS_API_KEY}`;
  const raw = await fetch(loadPath);
  const translations = await raw.json();
  return translations;
}

interface Props {
  children: ReactNode;
}

/**
 * @description Wrapper for `<I18nextProvider/>` that abstracts
 * initialisation of the i18n instance.
 */
export function LocaleProvider({ children }: Props) {
  const locale = useLocale();
  const namespace = 'web';

  return (
    <I18NextProvider
      locale={locale}
      namespace={namespace}
      importFn={(language, namespace, callback) => {
        /**
         * Prefer initial store over fetching json resource bundles.
         * Initial language and store is set when pre-rendering the app in SSR.
         * This should behave synchronously as the store is readily available.
         */
        if (
          typeof window !== 'undefined' &&
          typeof window?.initialI18nStore !== 'undefined' &&
          typeof window?.initialLanguage !== 'undefined' &&
          window?.initialLanguage === language &&
          RUNTIME_ENV === 'browser' &&
          window.initialLanguage in window.initialI18nStore
        ) {
          callback(
            null,
            window.initialI18nStore[window.initialLanguage]['web']
          );

          /**
           * In prod environments, for performance reasons, use the 'local' static
           * json for translations.
           * For other environments, we force a refetch to i18nexus so that we have a
           * quick feedback loop whilst tweaking translations.
           */
        } else if (SWS_ENV === 'prod' && typeof TEST_RUNNER === 'undefined') {
          try {
            import(`@assets/locales/${language}/${namespace}.json`)
              .then((locales) => callback(null, locales))
              .catch((error) => callback(error, null));
          } catch (error) {
            if (error instanceof Error) {
              callback(error, null);
            } else {
              callback(new Error('An unknown error has occurred'), null);
            }
          }
        } else {
          fetchLocales(language, namespace)
            .then((locales) => callback(null, locales))
            .catch((error) => callback(error, null));
        }
      }}
    >
      {children}
    </I18NextProvider>
  );
}
